const VERSION_STORAGE_KEY = 'dd_app_version';
const RELOAD_AT_KEY = 'dd_stale_asset_reload_at';

/** Suppress repeat reloads within this window to avoid loops. */
const RELOAD_COOLDOWN_MS = 30_000;

const STALE_ASSET_PATTERNS: RegExp[] = [
  /Importing a module script failed/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Unable to preload CSS/i,
  /Loading chunk [\w-]+ failed/i,
  /ChunkLoadError/i,
];

export function isStaleAssetMessage(message: string): boolean {
  if (!message) return false;
  return STALE_ASSET_PATTERNS.some(pattern => pattern.test(message));
}

/** True when the failure is a missing/stale hashed /assets/* chunk after a deploy. */
export function isStaleAssetError(error: unknown): boolean {
  if (!error) return false;
  if (typeof error === 'string') return isStaleAssetMessage(error);
  if (error instanceof Error) {
    return isStaleAssetMessage(error.message) || isStaleAssetMessage(error.name);
  }
  if (typeof error === 'object' && 'message' in error) {
    return isStaleAssetMessage(String((error as { message: unknown }).message));
  }
  return isStaleAssetMessage(String(error));
}

let staleAssetReloadPending = false;

/** True once a stale-asset hard reload is committed for this page load. */
export function isStaleAssetReloadPending(): boolean {
  return staleAssetReloadPending;
}

function recentlyReloadedForStaleAsset(): boolean {
  try {
    const raw = sessionStorage.getItem(RELOAD_AT_KEY);
    if (!raw) return false;
    const last = Number(raw);
    return Number.isFinite(last) && Date.now() - last < RELOAD_COOLDOWN_MS;
  } catch {
    return false;
  }
}

/**
 * Hard-reload once when a stale deploy asset is detected.
 * Returns true if a reload was triggered.
 */
export function reloadOnceForStaleAsset(_reason?: string): boolean {
  if (typeof window === 'undefined') return false;
  if (recentlyReloadedForStaleAsset()) return false;

  try {
    sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()));
  } catch {
    // sessionStorage may be blocked; still attempt reload
  }

  staleAssetReloadPending = true;
  window.location.reload();
  return true;
}

/**
 * Recover from Vite modulepreload / dynamic-import failures after a deploy
 * (Safari: "Importing a module script failed", Vite: "Unable to preload CSS").
 */
export function installStaleAssetRecovery(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('vite:preloadError', () => {
    // Schedule the reload but never call event.preventDefault(). When the default
    // is prevented, __vitePreload resolves the failed import with undefined, so the
    // lazy() mapper reads a property off undefined and throws a TypeError. Let the
    // load error propagate: React.lazy rejects and the error boundary shows
    // ErrorFallback with a retry while the reload races it.
    reloadOnceForStaleAsset('vite:preloadError');
  });

  window.addEventListener('unhandledrejection', event => {
    if (!isStaleAssetError(event.reason)) return;
    if (reloadOnceForStaleAsset('unhandledrejection')) {
      event.preventDefault();
    }
  });

  window.addEventListener(
    'error',
    event => {
      const message = event.message || (event.error instanceof Error ? event.error.message : '');
      if (!isStaleAssetError(event.error) && !isStaleAssetMessage(message)) return;
      if (reloadOnceForStaleAsset('error')) {
        event.preventDefault();
      }
    },
    true
  );
}

/** After deploy, stale immutable /assets/* chunks cause runtime errors — reload once when VERSION.txt changes. */
export async function syncDeployVersion(): Promise<void> {
  try {
    const res = await fetch('/VERSION.txt', { cache: 'no-store' });
    if (!res.ok) return;
    const remote = (await res.text()).trim();
    if (!remote) return;

    const stored = sessionStorage.getItem(VERSION_STORAGE_KEY);
    if (stored && stored !== remote) {
      sessionStorage.setItem(VERSION_STORAGE_KEY, remote);
      // Allow chunk-error recovery again after a real deploy bump.
      sessionStorage.removeItem(RELOAD_AT_KEY);
      window.location.reload();
      return;
    }
    sessionStorage.setItem(VERSION_STORAGE_KEY, remote);
  } catch {
    // Non-fatal — app still loads
  }
}
